/**
 * ⚡ LATENCY TRACKER (Telemetría de Velocidad)
 * ============================================
 * Utility to measure execution time of Core UseCases.
 * Usage:
 *    const tracker = new LatencyTracker('GetDashboard');
 *    // ... operations ...
 *    tracker.finish(); // Logs: [Perf] GetDashboard: 45ms
 */

export class LatencyTracker {
    private start: number;
    private name: string;

    constructor(name: string) {
        this.name = name;
        this.start = performance.now();
    }

    finish(metadata?: Record<string, unknown>) {
        const duration = Math.round(performance.now() - this.start);
        LatencyTracker.log(this.name, duration, metadata);
        return duration;
    }

    static track(name: string, context: string, startTime: number, metadata?: Record<string, unknown>) {
        const duration = Math.round(performance.now() - startTime);
        const fullName = `${context}:${name}`;
        this.log(fullName, duration, metadata);
    }

    private static log(name: string, duration: number, metadata?: Record<string, unknown>) {
        console.log(`[⚡ Perf] ${name}: ${duration}ms`, metadata || '');
        if (typeof window !== 'undefined' && window.performance) {
            window.performance.measure(name, { start: performance.now() - duration });
        }
        // 3. TODO: Send to Analytics Service (PostHog/Supabase)
        // Analytics.track('performance_metric', { name: name, duration, ...metadata });
    }

    static getFingerprint(): string {
        try {
            return btoa(navigator.userAgent + navigator.language).substring(0, 16);
        } catch {
            return 'fallback-fp-' + Math.random().toString(36).substring(2, 9);
        }
    }
}

/**
 * Decorator / Wrapper for UseCases
 */
export async function measure<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const tracker = new LatencyTracker(name);
    try {
        const result = await fn();
        tracker.finish({ success: true });
        return result;
    } catch (error) {
        tracker.finish({ success: false, error });
        throw error;
    }
}
